import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Container, Title } from './AddModelToBuyerModal.styled';
import { Modal } from '../Modal/Modal';
import { useTranslation } from 'react-i18next';
import ScrollableList from 'components/ScrollableList/ScrollableList';
import { useDebounce } from 'utils/hooks/useDebounce';
import { useAddModelToBuyer, useGetBuyersEligibleModels } from './hooks';
import { ELIGIBLE_MODELS_FOR_BUYER_MODEL_PER_PAGE } from './constants';
import { IModel } from 'types/Model.types';
import { getDataFromPages } from 'utils/getDataFromPages';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';

export interface IAddModelToBuyerModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  buyerId: number;
  companyId: number;
}

export function AddModelToBuyerModal({
  isOpen,
  setIsOpen,
  buyerId,
  companyId,
}: IAddModelToBuyerModalProps) {
  const { t } = useTranslation();
  const [searchBy, setSearchBy] = useState<string>('');
  const debouncedSearchBy = useDebounce(searchBy);
  const { isPhone } = useBreakpointFlag();

  const {
    data: modelsData,
    isLoading: isLoadingGetModels,
    hasNextPage,
    fetchNextPage,
  } = useGetBuyersEligibleModels(
    ELIGIBLE_MODELS_FOR_BUYER_MODEL_PER_PAGE,
    companyId,
    buyerId,
    debouncedSearchBy,
    isOpen
  );

  const models: IModel[] = getDataFromPages(modelsData?.pages, 'models');

  const { mutate: addModelToBuyer, isLoading: isLoadingAddModelToBuyer } =
    useAddModelToBuyer(companyId, buyerId);

  const handleSelect = (model: IModel) => {
    if (!isLoadingAddModelToBuyer) {
      addModelToBuyer(model.id, { onSuccess: () => setIsOpen(false) });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchBy('');
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      modalStyle={{
        margin: 'auto',
        width: isPhone ? '90%' : '600rem',
        minWidth: isPhone ? '90%' : 'auto',
        position: 'fixed',
      }}
    >
      <Container>
        <Title>{t('Select model for granting access')}</Title>
        <ScrollableList
          items={models || []}
          isLoading={isLoadingGetModels || isLoadingAddModelToBuyer}
          noContentLabel={t('No models found')}
          onClick={(model: IModel) => handleSelect(model)}
          onSearch={(str: string) => setSearchBy(str)}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetching={isLoadingAddModelToBuyer}
          showModelId={true}
        />
      </Container>
    </Modal>
  );
}
